import { AddIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spacer,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import useApi from "../../../shared/hooks/useApi";
import { components, ExternalPatientServicePlanId } from "../../../shared/schema/schema";
import { dateFormatter } from "../../../shared/utils/date-formatter";
import { formatErrorResponse } from "../../../shared/utils/format-response-error";
import { GET_CO_SERVICE_PLAN_IMPORTS_QUERY } from "../service-plan.graphql";
import ScheduleEditorConfirmDialog from "./ScheduleEditorConfirmDialog";
import ScheduleEditorShiftCard from "./ScheduleEditorShiftCard";
import { DAYS_MONDAY_FIRST, hours, shiftMinutes, title } from "./servicePlanWeek.utils";
import { useScheduleEditorDraft } from "./useScheduleEditorDraft";

type CoServicePlanImportStagedShift = components["schemas"]["CoServicePlanImportStagedShift"];
type CoServicePlanImportCaregiver = components["schemas"]["CoServicePlanImportCaregiver"];
type CoServicePlanImportCoordinatorEdit =
  components["schemas"]["CoServicePlanImportCoordinatorEdit"];
type CoServicePlanImportDuty = components["schemas"]["CoServicePlanImportDuty"];
type CoServicePlanImportLine = components["schemas"]["CoServicePlanImportLine"];
type CoServicePlanImportViolation = components["schemas"]["CoServicePlanImportViolation"];

interface Props {
  caregivers: CoServicePlanImportCaregiver[];
  coordinatorEdit: CoServicePlanImportCoordinatorEdit | null;
  duties: CoServicePlanImportDuty[];
  importId: ExternalPatientServicePlanId;
  lines: CoServicePlanImportLine[];
  /**
   * The week as it was FIRST staged, which is what the read-only column is for.
   *
   * Null on a submission staged before that was recorded, and then the staged week
   * itself stands in: nobody has edited it, so the two are the same week.
   */
  original: CoServicePlanImportStagedShift[] | null;
  /** The week being edited — what is staged right now. */
  staged: CoServicePlanImportStagedShift[];
  onClose: () => void;
}

/**
 * The proposed week, beside a week a coordinator can change.
 *
 * Split down the middle and on one row per weekday, for the same reason the drawer's
 * comparison is: the question being answered is "what does Tuesday become?", which is
 * unanswerable from two lists read one after the other. The left column never moves —
 * it is what the scheduler produced, kept so an edit can always be read against it —
 * and everything on the right is editable.
 *
 * Below a wide screen the two stack per day rather than per week, so the pairing
 * survives: the original Tuesday stays directly above the Tuesday being written.
 *
 * Mounted only while it is open, so the draft is seeded fresh every time it opens and
 * a refetch underneath a coordinator cannot overwrite what they have typed.
 */
export default function CoServicePlanScheduleEditorModal(props: Props) {
  const { mutations, queries } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();
  const draft = useScheduleEditorDraft(props.staged, props.lines);
  // Asked in the footer rather than in a dialog of its own, the way the drawer asks
  // before regenerating: a second overlay over a full-screen modal is a layer nobody
  // needs, and the question is about the thing being looked at.
  const [confirmingDiscard, setConfirmingDiscard] = React.useState(false);
  // What came back from a submit that asked rather than wrote; null while nothing
  // has been asked. One piece of state rather than a disclosure plus a list, so the
  // dialog cannot be open without the findings it exists to show.
  const [findings, setFindings] = React.useState<CoServicePlanImportViolation[] | null>(null);

  const submit = useMutation({
    ...mutations.servicePlan.replaceCoImportSchedule(),
    onSuccess: (result) => {
      switch (result.kind) {
        case "VIOLATIONS":
          // Nothing was written server-side. This is the screen's question.
          setFindings(result.violations);
          return;
        case "REPLACED":
          // The drawer behind this is now showing a week that no longer exists,
          // and so is the list behind the drawer: its Flags column counts the
          // snapshot's own findings, and re-staging rewrote them.
          queryClient.invalidateQueries({
            queryKey: queries.servicePlan.coImportDetails(props.importId).queryKey,
          });
          queryClient.invalidateQueries({ queryKey: [GET_CO_SERVICE_PLAN_IMPORTS_QUERY] });
          toast({
            title: "Your week is now the one waiting for approval",
            description:
              `${result.shiftsStaged} ${result.shiftsStaged === 1 ? "shift" : "shifts"} staged ` +
              `across ${result.groupsStaged} caregiver/authorization ` +
              `${result.groupsStaged === 1 ? "pairing" : "pairings"}. Nothing about this patient ` +
              "changes until the submission is approved.",
            status: "success",
            position: "top-right",
            isClosable: true,
          });
          props.onClose();
          return;
      }
    },
    onError: (error) =>
      toast({
        title: "Could not submit this week",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
        isClosable: true,
      }),
  });

  /**
   * The whole week, either asking about its findings or answering for them.
   *
   * The same draft both times: **Submit anyway** re-sends exactly what was checked,
   * because the dialog is over the grid and nothing can have moved underneath it.
   */
  const send = (acceptViolations: boolean) =>
    submit.mutate({
      importId: props.importId,
      shifts: draft.toRequestShifts(),
      acceptViolations,
    });

  const original = props.original ?? props.staged;
  const originalMinutes = original.reduce((total, shift) => total + shift.durationMinutes, 0);
  const draftMinutes = draft.shifts.reduce(
    (total, shift) => total + (shiftMinutes(shift.startTime, shift.endTime) ?? 0),
    0
  );
  const incomplete = draft.shifts.filter(
    (shift) =>
      shift.caregiverId === null ||
      shift.serviceCode === null ||
      shiftMinutes(shift.startTime, shift.endTime) === null
  ).length;

  /**
   * Why Submit is disabled, or null when it is not.
   *
   * Printed on the footer line a coordinator is already reading rather than hidden in
   * a tooltip on a disabled button, which is the one control that cannot be hovered
   * on a touch device and the one whose tooltip is most often suppressed.
   *
   * Incomplete shifts are named first: a week that is both unchanged and incomplete
   * was already incomplete when the scheduler staged it, and the shifts are the thing
   * to do something about.
   */
  const blocked =
    incomplete > 0
      ? `${incomplete} still ${incomplete === 1 ? "needs" : "need"} a caregiver, a service ` +
        "code or a workable pair of times"
      : draft.isDirty
      ? null
      : "No changes to submit";

  const requestClose = () => {
    // A submit in flight owns the week until it answers. Closing under it would
    // leave a coordinator with no sight of what it staged, or of what it asked.
    if (submit.isPending) {
      return;
    }

    if (draft.isDirty) {
      setConfirmingDiscard(true);
      return;
    }

    props.onClose();
  };

  return (
    <Modal isOpen scrollBehavior="inside" size="full" onClose={requestClose}>
      <ModalOverlay zIndex="aboveLegacyFooter" />
      {/* Over the legacy AngularJS footer, and over the drawer this opened from —
          both of which sit at that same z-index, and this is portalled after them. */}
      <ModalContent containerProps={{ zIndex: "aboveLegacyFooter" }}>
        <ModalHeader borderBottomWidth="1px">
          Edit the proposed week
          <Text color="gray.500" fontSize="sm" fontWeight="normal">
            Nothing is saved until you submit, and nothing about this patient changes until the
            submission is approved.
          </Text>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack align="stretch" spacing={4}>
            {props.coordinatorEdit !== null && (
              <Alert status="info">
                <AlertIcon />
                <Text fontSize="sm">
                  This week was already edited by{" "}
                  {props.coordinatorEdit.editedByName ?? "a coordinator who has since been removed"}{" "}
                  on {dateFormatter.toDateTime(props.coordinatorEdit.editedAt)}. The left column is
                  still the week the scheduler built.
                </Text>
              </Alert>
            )}

            <SimpleGrid
              bg="white"
              borderBottomWidth="1px"
              columns={{ base: 1, lg: 2 }}
              position="sticky"
              py={2}
              spacing={4}
              top={0}
              zIndex={1}
            >
              <ColumnHeading
                count={original.length}
                minutes={originalMinutes}
                title="The week the scheduler built"
              />
              <ColumnHeading count={draft.shifts.length} minutes={draftMinutes} title="Your week" />
            </SimpleGrid>

            {DAYS_MONDAY_FIRST.map((day) => {
              const originalForDay = original
                .filter((shift) => shift.dayOfWeek.toUpperCase() === day)
                .sort((a, b) => a.startTime.localeCompare(b.startTime));
              const draftForDay = draft.shifts.filter((shift) => shift.dayOfWeek === day);

              return (
                <Box key={day} borderTopWidth="1px" pt={3}>
                  <Text fontWeight="semibold" mb={2}>
                    {title(day)}
                  </Text>
                  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                    <Box>
                      {/* Named only where the columns have stacked and the headings
                          above are no longer beside what they name. */}
                      <StackedCaption>As the scheduler built it</StackedCaption>
                      {originalForDay.length === 0 ? (
                        <Text color="gray.400">—</Text>
                      ) : (
                        <VStack align="stretch" spacing={2}>
                          {originalForDay.map((shift) => (
                            <OriginalShift key={shift.index} shift={shift} />
                          ))}
                        </VStack>
                      )}
                    </Box>
                    <Box>
                      <StackedCaption>Yours</StackedCaption>
                      <VStack align="stretch" spacing={2}>
                        {draftForDay.map((shift) => (
                          <ScheduleEditorShiftCard
                            key={shift.id}
                            caregivers={props.caregivers}
                            duties={props.duties}
                            lines={props.lines}
                            shift={shift}
                            onChange={(patch) => draft.updateShift(shift.id, patch)}
                            onRemove={() => draft.removeShift(shift.id)}
                          />
                        ))}
                        <Button
                          alignSelf="flex-start"
                          leftIcon={<AddIcon />}
                          size="xs"
                          variant="ghost"
                          onClick={() => draft.addShift(day)}
                        >
                          Add a shift on {title(day)}
                        </Button>
                      </VStack>
                    </Box>
                  </SimpleGrid>
                </Box>
              );
            })}
          </VStack>
        </ModalBody>

        <ModalFooter borderTopWidth="1px">
          <VStack align="stretch" spacing={3} width="full">
            {confirmingDiscard && (
              <Alert status="warning">
                <AlertIcon />
                <Text fontSize="sm">
                  Your changes to this week have not been submitted. Closing now throws them away.
                </Text>
              </Alert>
            )}
            <HStack spacing={3}>
              <Text color={incomplete > 0 ? "orange.600" : "gray.500"} fontSize="sm">
                {draft.shifts.length} {draft.shifts.length === 1 ? "shift" : "shifts"} ·{" "}
                {hours(draftMinutes)} / wk
                {blocked !== null && ` · ${blocked}`}
              </Text>
              <Spacer />
              {confirmingDiscard ? (
                <>
                  <Button variant="ghost" onClick={() => setConfirmingDiscard(false)}>
                    Keep editing
                  </Button>
                  <Button colorScheme="red" onClick={props.onClose}>
                    Discard my changes
                  </Button>
                </>
              ) : (
                <>
                  <Button isDisabled={submit.isPending} variant="ghost" onClick={requestClose}>
                    Close
                  </Button>
                  {/* Unchanged is as good a reason not to submit as incomplete is.
                      Submitting re-stages: the staged plan of care is rebuilt, the
                      suggestion rows are replaced, and `coordinatorEdit` is set — so
                      a no-op would put the drawer's Edited tag on a week nobody
                      touched, and that tag exists to tell whoever approves that a
                      person changed something. */}
                  <Button
                    colorScheme="blue"
                    isDisabled={blocked !== null}
                    isLoading={submit.isPending}
                    onClick={() => send(false)}
                  >
                    Submit
                  </Button>
                </>
              )}
            </HStack>
          </VStack>
        </ModalFooter>

        {/* Keep editing leaves the draft exactly as it is — the dialog is a question
            about this grid, not a step away from it. */}
        <ScheduleEditorConfirmDialog
          isOpen={findings !== null}
          isSubmitting={submit.isPending}
          violations={findings ?? []}
          onKeepEditing={() => setFindings(null)}
          onSubmitAnyway={() => send(true)}
        />
      </ModalContent>
    </Modal>
  );
}

function ColumnHeading(props: { count: number; minutes: number; title: string }) {
  return (
    <HStack spacing={2}>
      <Text fontWeight="semibold">{props.title}</Text>
      <Text color="gray.500" fontSize="sm">
        {hours(props.minutes)} / wk · {props.count} {props.count === 1 ? "shift" : "shifts"}
      </Text>
    </HStack>
  );
}

function StackedCaption(props: { children: React.ReactNode }) {
  return (
    <Text color="gray.500" display={{ base: "block", lg: "none" }} fontSize="xs" mb={1}>
      {props.children}
    </Text>
  );
}

/** One shift of the original week: what it is, and nothing that can be changed. */
function OriginalShift(props: { shift: CoServicePlanImportStagedShift }) {
  const { shift } = props;
  const caregiver =
    shift.caregiverName !== null && shift.caregiverName !== ""
      ? shift.caregiverName
      : shift.caregiverId === null
      ? "Unassigned"
      : `Caregiver #${shift.caregiverId}`;

  return (
    <Box bg="gray.50" borderWidth="1px" p={3} rounded="md">
      <HStack spacing={2} wrap="wrap">
        <Text fontWeight="semibold">
          {shift.startTime} – {shift.endTime}
        </Text>
        <Text color="gray.500" fontSize="sm">
          {hours(shift.durationMinutes)}
        </Text>
      </HStack>
      <Text color="gray.600" fontSize="sm">
        {caregiver}
        {shift.authorizationId !== null && ` · Auth #${shift.authorizationId}`}
      </Text>
      <HStack mt={1} spacing={1} wrap="wrap">
        {shift.duties.map((duty, index) => (
          <Badge key={`${index}-${duty.dutyCode}`} colorScheme="gray">
            {duty.occurrences > 1 ? `${duty.dutyCode} ×${duty.occurrences}` : duty.dutyCode}
          </Badge>
        ))}
      </HStack>
    </Box>
  );
}
